import type { ReactNode } from "react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import Joyride from "react-joyride";

import type { ITourJoyrideProps } from "./types";

// NOTE: NSAI-5822 — the tooltip is a floating surface, so it takes --popover
// like every dropdown and popover in the app. It used to take --background,
// which is the PAGE colour: against the tour's own dim overlay the card read as
// a hole punched in the page rather than as something sitting above it.
//
// The accent is --primary-dark (#5722EF). That is the primary the designer
// specifies for buttons on these modal surfaces — an earlier pass moved the
// tour onto the blue --primary to get away from a purple that looked wrong,
// but the purple was never the problem: the tooltip's halo was (see the shadow
// below), and the welcome dialog was painting its HIGHLIGHTED TEXT in the
// button colour instead of --highlighted.
const tourJoyrideStyles = {
  options: {
    arrowColor: "hsl(var(--popover))",
    backgroundColor: "hsl(var(--popover))",
    primaryColor: "hsl(var(--primary-dark))",
    textColor: "hsl(var(--popover-foreground))",
    zIndex: 1000,
  },
  tooltip: {
    padding: "1.25rem",
    width: "450px",
    borderRadius: "var(--radius)",
    border: "1px solid hsl(var(--border))",
    // NOTE: A shadow is dark in BOTH themes. Deriving it from --foreground made
    // it near-white at night, so the card wore a pale halo that read as a
    // gradient bleeding out of its edges rather than as depth.
    boxShadow:
      "0 10px 30px -12px rgb(0 0 0 / 0.45), 0 2px 6px -2px rgb(0 0 0 / 0.3)",
  },
  tooltipContainer: {
    textAlign: "start",
  },
  tooltipTitle: {
    fontSize: "1.125rem",
    lineHeight: "1.75rem",
    fontWeight: "600",
    marginBottom: "8px",
  },
  tooltipContent: {
    fontSize: "1rem",
    lineHeight: "1.5rem",
    padding: "0",
  },
  buttonNext: {
    backgroundColor: "hsl(var(--primary-dark))",
    color: "hsl(var(--primary-dark-foreground))",
    borderRadius: "calc(var(--radius) - 2px)",
    padding: "0.5rem 1rem",
    minHeight: "2.5rem",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
  },
  // NOTE: NSAI-5822 — `hsl(...)`, not the bare token. The repo's CSS variables
  // hold HSL COMPONENTS ("215 16% 47%"), so `color: var(--muted-foreground)`
  // is not a colour at all: the declaration was invalid and dropped, and Back
  // and Skip inherited the tooltip's full-strength body ink instead of sitting
  // quietly beside the primary action.
  buttonBack: {
    padding: "0",
    color: "hsl(var(--muted-foreground))",
    marginInlineEnd: "1rem",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
  },
  buttonSkip: {
    padding: "0",
    color: "hsl(var(--muted-foreground))",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
  },
  buttonClose: {
    display: "none",
  },
} as const;

const tourJoyrideFloaterProps = {
  hideArrow: false,
  disableAnimation: true,
  styles: {
    arrow: {
      length: 8,
      spread: 12,
    },
  },
} as const;

const TourJoyride = memo(
  ({
    steps,
    stepIndex,
    isRunning,
    isPaused = false,
    callback,
  }: ITourJoyrideProps): ReactNode => {
    const { t } = useTranslation("tour");

    return (
      <Joyride
        key={isRunning ? "running" : "not-running"}
        steps={steps}
        stepIndex={stepIndex}
        run={isRunning && !isPaused}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep={false}
        disableScrolling={true}
        spotlightPadding={5}
        disableOverlayClose={false}
        disableCloseOnEsc={true}
        disableScrollParentFix
        styles={tourJoyrideStyles}
        floaterProps={tourJoyrideFloaterProps}
        locale={{
          nextLabelWithProgress: t("button.nextLabelWithProgressWeb"),
          open: t("button.open"),
          back: t("button.back"),
          close: t("button.close"),
          last: t("button.last"),
          next: t("button.next"),
          skip: t("button.skip"),
        }}
        callback={callback}
      />
    );
  },
);

TourJoyride.displayName = "TourJoyride";

export default TourJoyride;
